import { Injectable } from '@angular/core';
import { BehaviorSubject, firstValueFrom, filter } from 'rxjs';
import { GenericService } from './generic.service';
import { RequestMethodEnum, RequestOptions } from '@model';
import { HttpClient } from '@angular/common/http';
import { ContextSelectorService } from 'ngx-global-nav';

@Injectable()
export class SupportingDataService {
  dataProviders$: BehaviorSubject<any[]>;
  channels$: BehaviorSubject<any[]>;
  reportingIntervals$: BehaviorSubject<any[]>;
  offerUnits$: BehaviorSubject<any[]>;
  private externalPointsWhitelist: String[] = [];
  private baselineWhitelist: String[] = [];
  assetManagerSubdomains$: BehaviorSubject<any[]>;
  private readonly currentOrgId$: BehaviorSubject<string | null>;
  private isInitialized = false;

  constructor(
    private genericService: GenericService,
    private http: HttpClient,
    private orgSelectorService: ContextSelectorService,
  ) {
    this.dataProviders$ = new BehaviorSubject<any>([]);
    this.channels$ = new BehaviorSubject<any[]>([]);
    this.reportingIntervals$ = new BehaviorSubject<any[]>([]);
    this.offerUnits$ = new BehaviorSubject<any[]>([]);
    this.assetManagerSubdomains$ = new BehaviorSubject<any[]>([]);
    this.currentOrgId$ = new BehaviorSubject<string | null>(null);

    this.orgSelectorService.currentContext$
      .pipe(filter((orgs) => Array.isArray(orgs) && orgs.length > 0 && !!orgs[0]?.id))
      .subscribe((orgs) => {
        this.currentOrgId$.next(orgs[0].id);
        if (!this.isInitialized) {
          this.isInitialized = true;
          this.setDataProviders().catch((err) => console.error('Error loading data providers:', err));
        }
      });
  }

  async setDataProviders() {
    try {
      const currentOrgId = this.currentOrgId$.getValue();
      if (!currentOrgId) {
        console.warn('No organization ID available');
        this.dataProviders$.next([]);
        return;
      }

      const options: RequestOptions<null, any[]> = {
        method: RequestMethodEnum.GET,
        path: `/data-providers/${currentOrgId}`,
      };
      const dataProviders = await firstValueFrom(this.genericService.request(options));
      this.dataProviders$.next(Array.isArray(dataProviders) ? dataProviders : []);
    } catch (err) {
      console.error('Could not fetch data providers:', err);
      this.dataProviders$.next([]);
    }
  }

  async getChannels() {
    try {
      const options: RequestOptions<null, any[]> = {
        method: RequestMethodEnum.GET,
        path: `/channel-categories`,
      };
      const channels = await firstValueFrom(this.genericService.request(options));
      this.channels$.next(Array.isArray(channels) ? channels : []);
    } catch (err) {
      console.log('Could not load channels.', err);
    }
  }

  getExternalPointsChannels() {
    if (this.channels$.getValue()) {
      return this.channels$.getValue().filter((channel) => this.externalPointsWhitelist.includes(channel.id));
    }
    return [];
  }

  async setReportingIntervals() {
    try {
      const options: RequestOptions<null, any[]> = {
        method: RequestMethodEnum.GET,
        path: `/granularities`,
      };
      const reportingIntervals = await firstValueFrom(this.genericService.request(options));
      this.reportingIntervals$.next(Array.isArray(reportingIntervals) ? reportingIntervals : []);
    } catch (err) {
      console.log('Could not load reporting intervals.', err);
    }
  }

  async getOfferUnits() {
    try {
      const options: RequestOptions<null, any[]> = {
        method: RequestMethodEnum.GET,
        path: `/offer-units`,
      };
      const offerUnits = await firstValueFrom(this.genericService.request(options));
      this.offerUnits$.next(Array.isArray(offerUnits) ? offerUnits : []);
    } catch (err) {
      console.log('Could not load offer units.', err);
    }
  }

  async fetchChannelWhitelists() {
    try {
      const options: RequestOptions<null, any[]> = {
        method: RequestMethodEnum.GET,
        path: `/point-channel-whitelists`,
      };
      const response = await firstValueFrom(this.genericService.request(options));
      const whitelists: any[] = Array.isArray(response) ? response : [];
      if (whitelists && whitelists.length > 0) {
        const eternalPoints = whitelists.find((whitelist: any) => whitelist.pointType == 'EXTERNAL_POINT');
        this.externalPointsWhitelist = eternalPoints?.channels || [];
      }
    } catch (err) {
      console.log('Could not load channel whitelists.', err);
    }
  }

  async getAssetManagerSubdomains() {
    try {
      console.info(`Retrieving asset manager subdomains from ITS service...`);
      const options: RequestOptions<null, any[]> = {
        method: RequestMethodEnum.GET,
        path: `/domains`,
      };
      const domains = await firstValueFrom(this.genericService.request(options));
      const assetManagerSubdomains = (Array.isArray(domains) ? domains : [])
        .filter((d: any) => d.domain === 'ASSET_MANAGER_REGISTRATION_ID')
        .map((d: any) => d.subdomain);
      this.assetManagerSubdomains$.next(assetManagerSubdomains);
    } catch (err) {
      console.log('Could not load assetManager subdomains from ITS.', err);
    }
  }
}
